import cn from 'classnames';
import { useContext } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { BrandContext, HOME_CHEF } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import useBrand from 'hooks/useBrand';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import useQuery from 'hooks/useQuery';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import BackgroundBeigeReverse from 'images/signUpFunnel/curvedBackgroundBeigeReverse.jpg';
import { FAMILY, TEMPO } from 'lib/constants';
import { PARTNERSHIP_RESOURCE_TYPES } from 'lib/constants/signUp';
import styles from 'styles/components/signUpFunnel/BackgroundContainer';

const getPartnerReferrer = () => {
  const reactFunnelElement = document?.getElementById('hc-sign-up-funnel-react-app');
  return reactFunnelElement?.getAttribute('data-partner-referrer');
};

const BackgroundContainer = ({ children }) => {
  const partnerReferrer = getPartnerReferrer();
  const { brand } = useContext(BrandContext);
  const { isQueryParamTrue: isAdSource } = useQuery('source', 'ad');
  const isTempo = useBrand(TEMPO);
  const mealPlan = useFunnelMealPlan();
  const isFamilyPlan = mealPlan === FAMILY;

  const { isMobile } = useScreenSizeConstants();
  const brandRoutes = Routes.getBrandRoute({ brand: HOME_CHEF, isFamily: false, addSuffix: false });

  const isIntro = useRouteMatch(brandRoutes.intro);
  const isPickPlan = useRouteMatch(brandRoutes.pickPlan);
  const isPreferenceDietary = useRouteMatch(brandRoutes.preferenceDietary);
  const isPreferenceAvoid = useRouteMatch(brandRoutes.preferenceAvoid);
  const isPromo = useRouteMatch(brandRoutes.promo);
  const isBox = useRouteMatch(brandRoutes.box);
  const boxRecommenderPeople = useRouteMatch(brandRoutes.boxRecommenderPeople);
  const boxRecommenderMeals = useRouteMatch(brandRoutes.boxRecommenderMeals);
  const boxRecommenderDays = useRouteMatch(brandRoutes.boxRecommenderDays);

  const { data: partnershipData } = usePartnershipQuery(partnerReferrer);

  const introImages = partnershipData?.partnership?.resources.flatMap(resource =>
    resource.resourceType === PARTNERSHIP_RESOURCE_TYPES.INTRO_IMAGE ? [resource.url] : []
  );
  const isBoxRecommenderEnabled = Boolean(
    boxRecommenderPeople || boxRecommenderMeals || boxRecommenderDays
  );
  const isBilling = useRouteMatch(brandRoutes.billing);
  const isThankYou = useRouteMatch(brandRoutes.thankyou);

  const backgroundClasses = cn('flex grow flex-col overflow-x-hidden', {
    'pb-6': !isThankYou,
    [styles[brand]]: brand,
    [styles.intro]: isIntro && isTempo && !isAdSource,
    'bg-cream-100': isPickPlan,
    [styles.preferences]: isPreferenceDietary || isPreferenceAvoid,
    [styles.preferencesFamily]: (isPreferenceDietary || isPreferenceAvoid) && isFamilyPlan,
    [styles.promo]: isPromo,
    [styles.promoFamily]: isPromo && isFamilyPlan,
    [styles.box]: isBox,
    [styles.boxRecommender]: isBoxRecommenderEnabled,
    [styles.checkout]: isBilling,
    [styles.thankYou]: isThankYou,
  });

  const backgroundStyle =
    isIntro && introImages?.length
      ? {
          backgroundImage: !isMobile
            ? `url('${introImages[0] || ''}'), url('${introImages[1] || ''}'), url('${BackgroundBeigeReverse}')`
            : `url('${BackgroundBeigeReverse}')`,
          backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
          backgroundSize: '350px 350px, 350px 350px, 155% 330%',
          backgroundPosition: 'top 276px left -40px, right -50px top 36px, top center',
        }
      : {};

  return (
    <div className={backgroundClasses} style={backgroundStyle}>
      {children}
    </div>
  );
};

BackgroundContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BackgroundContainer;
